import { Component } from '@theme/component';

const KEY = 'tintaspace.favorites.v1';
const CHANGE = 'ts:favorites-change';
const ERROR = 'ts:favorites-error';

function readFavorites() {
  const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  return raw.filter((item) => {
    if (!item || typeof item.id !== 'string' || typeof item.title !== 'string' || typeof item.url !== 'string' || seen.has(item.id)) return false;
    try {
      const url = new URL(item.url, location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return false;
    } catch { return false; }
    seen.add(item.id);
    return true;
  });
}

function changeFavorite(item, removeOnly = false) {
  try {
    const items = readFavorites();
    const exists = items.some((entry) => entry.id === item.id);
    const next = items.filter((entry) => entry.id !== item.id);
    if (!exists && !removeOnly) next.push(item);
    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(CHANGE, { detail: { added: !exists && !removeOnly } }));
    return true;
  } catch {
    window.dispatchEvent(new Event(ERROR));
    return false;
  }
}

class FavoriteButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    window.addEventListener(CHANGE, () => this.refresh(), { signal });
    window.addEventListener('storage', () => this.refresh(), { signal });
    window.addEventListener('pageshow', () => this.refresh(), { signal });
    this.refresh();
    this.refs.button.hidden = false;
  }
  updatedCallback() { super.updatedCallback(); this.refresh(); this.refs.button.hidden = false; }
  disconnectedCallback() { this.abort?.abort(); super.disconnectedCallback(); }
  refresh() {
    let saved = false;
    try { saved = readFavorites().some((item) => item.id === this.dataset.id); } catch {}
    const label = saved ? this.dataset.remove : this.dataset.add;
    this.refs.button.setAttribute('aria-pressed', String(saved));
    this.refs.button.setAttribute('aria-label', `${label}: ${this.dataset.title}`);
    this.refs.label.textContent = label;
  }
  toggleFavorite(event) {
    event.preventDefault();
    event.stopPropagation();
    changeFavorite({ id: this.dataset.id, title: this.dataset.title, url: this.dataset.url, image: this.dataset.image });
  }
}

class FavoritesPanel extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    window.addEventListener(CHANGE, (event) => {
      this.refresh();
      this.refs.announcement.textContent = event.detail.added ? this.dataset.added : this.dataset.removed;
    }, { signal });
    window.addEventListener('storage', () => this.refresh(), { signal });
    window.addEventListener('pageshow', () => this.refresh(), { signal });
    window.addEventListener(ERROR, () => {
      const message = this.dataset.error;
      this.refs.status.textContent = message;
      this.refs.announcement.textContent = message;
      if (!this.refs.dialog.open) this.refs.dialog.showModal();
    }, { signal });
    this.refs.dialog.addEventListener('close', () => this.refs.launcher.focus(), { signal });
    this.refresh();
    this.refs.launcher.hidden = false;
  }
  disconnectedCallback() { this.abort?.abort(); super.disconnectedCallback(); }
  open() { this.refresh(); this.refs.dialog.showModal(); }
  close() { this.refs.dialog.close(); }
  refresh() {
    let items = [];
    try { items = readFavorites(); this.refs.status.textContent = ''; }
    catch { this.refs.status.textContent = this.dataset.error; }
    this.refs.count.textContent = String(items.length);
    this.refs.empty.hidden = items.length > 0;
    const fragment = document.createDocumentFragment();
    for (const item of items) {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.href = item.url;
      if (item.image) {
        try {
          const src = new URL(item.image, location.origin);
          if (src.protocol === 'https:' && (src.origin === location.origin || src.hostname === 'cdn.shopify.com')) {
            const image = document.createElement('img');
            image.src = src.href;
            image.alt = '';
            image.width = 72;
            image.height = 84;
            image.loading = 'lazy';
            link.append(image);
          }
        } catch {}
      }
      const title = document.createElement('span');
      title.textContent = item.title;
      link.append(title);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = this.dataset.remove;
      remove.setAttribute('aria-label', `${this.dataset.remove}: ${item.title}`);
      remove.dataset.id = item.id;
      remove.setAttribute('on:click', '/removeFavorite');
      li.append(link, remove);
      fragment.append(li);
    }
    this.refs.list.replaceChildren(fragment);
  }
  removeFavorite(event) {
    event.preventDefault();
    const id = event.target.dataset.id;
    if (!id) return;
    if (changeFavorite({ id }, true)) {
      const next = this.refs.list.querySelector('button');
      (next || this.refs.close).focus();
    }
  }
}

if (!customElements.get('ts-favorite-component')) customElements.define('ts-favorite-component', FavoriteButton);
if (!customElements.get('ts-favorites-component')) customElements.define('ts-favorites-component', FavoritesPanel);
